import type Lenis from 'lenis';

/** Module-level handle to the active Lenis instance (set by ExperienceProvider). */
let lenis: Lenis | null = null;

export function setLenis(instance: Lenis | null) {
  lenis = instance;
}

export function getLenis() {
  return lenis;
}

/**
 * Optional in-page navigator (set by the homepage presentation). It gets first refusal on
 * every `#hash`: slide ids move the presentation instead of scrolling the document.
 */
let hashNavigator: ((id: string) => boolean) | null = null;

export function setHashNavigator(fn: ((id: string) => boolean) | null) {
  hashNavigator = fn;
}

/**
 * Smoothly scrolls to an in-page `#hash`. Returns false when the target isn't on this page,
 * so callers can let normal navigation happen.
 */
export function scrollToHash(hash: string): boolean {
  if (!hash.startsWith('#') || hash.length < 2) return false;
  const id = decodeURIComponent(hash.slice(1));
  if (hashNavigator?.(id)) return true;
  const target = document.getElementById(id);
  if (!target) return false;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (lenis && !reduced) {
    lenis.scrollTo(target, { offset: -72 });
  } else {
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }
  window.history.replaceState(null, '', hash);
  // Move focus for keyboard and screen-reader users without a second scroll jump.
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  return true;
}

/**
 * Click handler for links like `/homepage#ecosystem`: when already on that page,
 * scroll smoothly instead of navigating.
 */
export function handleHashLinkClick(event: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  const url = new URL(href, window.location.href);
  if (!url.hash || url.pathname !== window.location.pathname) return;
  if (scrollToHash(url.hash)) event.preventDefault();
}
